// src/pages/salas/SalasQr.jsx — códigos QR para imprimir o compartir (recepción / admin).
// Uno general para /salas y uno por sala (/salas?sala=<key>) que abre la
// agenda ya parada en esa sala. Cada tarjeta se puede imprimir, descargar
// como imagen, compartir con el menú nativo del celular o mandar por WhatsApp.
import React, { useRef } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-toastify';
import { useSalas, I } from './SalasShell';

const IconShare = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></svg>;
const IconWa = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20l1.4-4A8 8 0 1 1 8 18.6z" /><path d="M9 9.6c0 3 2.4 5.4 5.4 5.4.6 0 1-.7.6-1.2l-.9-1a.7.7 0 0 0-.8-.1l-.6.3a4 4 0 0 1-1.9-1.9l.3-.6a.7.7 0 0 0-.1-.8l-1-.9c-.5-.4-1.2 0-1.2.6z" /></svg>;
const IconDown = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M6 11l6 6 6-6M4 21h16" /></svg>;

/** Genera un PNG con margen blanco y el nombre debajo a partir del canvas del QR. */
function buildPng(canvas, label) {
    const pad = 32, textH = label ? 44 : 0;
    const out = document.createElement('canvas');
    out.width = canvas.width + pad * 2; out.height = canvas.height + pad * 2 + textH;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, pad, pad);
    if (label) {
        ctx.fillStyle = '#0b1a3a'; ctx.font = 'bold 22px Inter, Arial, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(label, out.width / 2, canvas.height + pad + 30);
    }
    return new Promise(res => out.toBlob(res, 'image/png'));
}

function QrCard({ url, title, subtitle, label, size, color, big }) {
    const canvasRef = useRef(null);
    const fileName = `qr-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
    const message = `${title}: ${url}`;

    const getBlob = async () => {
        const canvas = canvasRef.current?.querySelector('canvas');
        if (!canvas) throw new Error('QR no listo');
        return buildPng(canvas, label);
    };
    const download = async () => {
        try {
            const blob = await getBlob();
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = fileName; a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        } catch { toast.error('No se pudo generar la imagen'); }
    };
    const share = async () => {
        try {
            const blob = await getBlob();
            const file = new File([blob], fileName, { type: 'image/png' });
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title, text: message });
            } else if (navigator.share) {
                await navigator.share({ title, text: message, url });
            } else {
                await navigator.clipboard?.writeText(message);
                toast.info('Link copiado. En esta computadora no hay menú de compartir; usa WhatsApp o descarga la imagen.');
            }
        } catch (e) { if (e?.name !== 'AbortError') toast.error('No se pudo compartir'); }
    };
    const whatsapp = () => {
        // WhatsApp no acepta imagen por link: va el texto con el enlace; la imagen
        // se manda aparte con "Compartir" (móvil) o descargándola.
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    };

    return (
        <div className={`sl-qr-card ${big ? 'big' : ''}`}>
            {big && <img src="/caprepa.svg" alt="Caprepa" className="logo" style={{ filter: 'invert(1) brightness(.2)' }} />}
            <div><QRCodeSVG value={url} size={size} level="M" fgColor={color || '#0b1a3a'} /></div>
            {/* Canvas oculto para generar el PNG descargable/compartible */}
            <div ref={canvasRef} style={{ display: 'none' }}><QRCodeCanvas value={url} size={512} level="M" fgColor={color || '#0b1a3a'} includeMargin={false} /></div>
            {big ? <h2>{title}</h2> : <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
            <div className="url">{url}</div>
            <div className="sl-noprint" style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 }}>
                <button type="button" className="sl-btn sl-btn-ghost sm" onClick={whatsapp} style={{ color: '#128c7e' }}>{IconWa} WhatsApp</button>
                <button type="button" className="sl-btn sl-btn-ghost sm" onClick={share}>{IconShare} Compartir</button>
                <button type="button" className="sl-btn sl-btn-ghost sm" onClick={download}>{IconDown} Imagen</button>
            </div>
        </div>
    );
}

export default function SalasQr() {
    const { rooms } = useSalas();
    const origin = window.location.origin;
    const main = `${origin}/salas`;

    return (
        <>
            <div className="sl-toolbar sl-noprint">
                <div>
                    <div className="sl-date-label" style={{ textTransform: 'none' }}>Códigos QR</div>
                    <div className="sl-date-sub">Imprímelos para recepción y cada sala, o compártelos por WhatsApp. En celular, "Compartir" manda la imagen del QR.</div>
                </div>
                <button className="sl-btn sl-btn-primary" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>{I.print}<span>Imprimir todos</span></button>
            </div>

            <div className="sl-qr-grid">
                <QrCard big url={main} size={260} label="Salas de juntas" title="Reserva una sala de juntas"
                        subtitle="Escanea, entra con tu usuario de CaprepaDesk y aparta la sala. Si no tienes acceso, pide a recepción que reserve por ti." />
                {rooms.map(r => (
                    <QrCard key={r._id} url={`${origin}/salas?sala=${encodeURIComponent(r.key)}`} size={170} color={r.color} label={r.name}
                            title={r.name} subtitle={`${r.capacity} personas${r.location ? ` · ${r.location}` : ''} · escanea para ver la agenda y reservar`} />
                ))}
            </div>
        </>
    );
}
